import type { CSSProperties } from 'react';

/**
 * WEB-2303. Единственное место, где кабинет рисует логотип сервиса.
 *
 * До этой карточки знак и название писали руками в четырёх файлах (рельс
 * кабинета, шапка входа, рельс и шапка админ-панели), и каждое место знало
 * своё имя: где-то «web2app», где-то «Quiz Builder». Поэтому здесь — один
 * компонент с двумя видами:
 *
 * - `full` — полный логотип (волна + «Subster.ai»), для развёрнутого рельса
 *   и страниц входа;
 * - `mark`  — только знак квадратом, для свёрнутого рельса.
 *
 * Подпись `alt` одна и та же у обоих видов: для человека со скринридером это
 * одна и та же вещь — имя сервиса, а не «картинка знака».
 *
 * ТЁМНАЯ ТЕМА (находка свежего взгляда 23.09). Словомарка «Subster» в паке
 * бренда почти чёрная (#242422), а подложка кабинета в тёмной теме —
 * настоящий чёрный (`--color-bg-layout: #000000`, styles/global.css). Имя
 * сервиса на ней пропадало: в рельсе оставалась одна синяя «волна».
 *
 * Поэтому полный логотип рисуется ДВУМЯ картинками, а выбирает между ними
 * CSS по атрибуту `data-theme` на <html> (правила — в global.css, рядом с
 * палитрой темы). Почему не подпиской из JS: атрибут ставит синхронный
 * скрипт в index.html ДО первого кадра, а подписка сработала бы только
 * после загрузки бандла — на перезагрузке в тёмной теме логотип успел бы
 * мигнуть невидимым: ровно та вспышка, от которой этот скрипт и завели
 * (WEB-806). Скрытую картинку скринридер не читает, поэтому одинаковый
 * `alt` у обеих дублем не звучит.
 *
 * Знак (`mark`) одинаков в обеих темах: он весь синий и читается и на белом,
 * и на чёрном.
 */
export type BrandLogoVariant = 'full' | 'mark';

export const BRAND_NAME = 'Subster.ai';

/** Светлая тема: словомарка #242422, знак и «.ai» — синие #2A65F2. */
export const LOGO_LIGHT_SRC = '/brand/subster-logo.svg';
/** Тёмная тема: та же геометрия, словомарка белая; знак и «.ai» — синие. */
export const LOGO_DARK_SRC = '/brand/subster-logo-dark.svg';
/** Знак квадратом — один на обе темы. */
export const MARK_SRC = '/brand/subster-mark.svg';

export interface BrandLogoProps {
  /** Полный логотип с подписью или только знак. По умолчанию — полный. */
  variant?: BrandLogoVariant;
  /** Высота в пикселях; ширина считается сама, пропорции не ломаются. */
  height?: number;
  className?: string;
  style?: CSSProperties;
}

function classes(...names: (string | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

export function BrandLogo({
  variant = 'full',
  height = 32,
  className,
  style,
}: BrandLogoProps) {
  // Высота приходит снаружи, ширина считается сама. `display` здесь НЕ
  // задаётся намеренно: инлайновый стиль перебил бы правило темы из
  // global.css, и в тёмной теме были бы видны обе картинки разом.
  const imgStyle: CSSProperties = { height, width: 'auto', ...style };

  if (variant === 'mark') {
    return (
      <img
        src={MARK_SRC}
        alt={BRAND_NAME}
        className={classes('brand-logo', className)}
        style={imgStyle}
      />
    );
  }

  return (
    <>
      <img
        src={LOGO_LIGHT_SRC}
        alt={BRAND_NAME}
        className={classes('brand-logo', 'brand-logo--light', className)}
        style={imgStyle}
      />
      <img
        src={LOGO_DARK_SRC}
        alt={BRAND_NAME}
        className={classes('brand-logo', 'brand-logo--dark', className)}
        style={imgStyle}
      />
    </>
  );
}

export default BrandLogo;
